var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { videoFactory, videoHydratedAction, } from '@core/hydration';
import { logError } from '@core/logging';
import { getGlobalStore } from '@core/store';
import { AppContextType } from '@embed/components/helpers';
import { PlayerLayout } from '@embed/components/Player3/store/types';
import { GLOBAL_PLAYERS_STATE_SYNC, REMOVE_PLAYER_STATE_SYNC, UPDATE_PLAYER_STATE_SYNC, } from '@embed/components/VideoPlayer/hooks/usePlayerStateDispatcher';
import { openReasonPriority, VideoPlayerOpenReason, } from '@embed/components/VideoPlayer/types';
import { createEmbedEmotionCache } from '@embed/createEmbedEmotionCache';
import { logInfo, parseFWPlayerParam, uuidv4 } from '@embed/helpers';
import { EnumStorageKeys } from '@embed/hooks/useStorage';
import { sendPixelTrackingWithSession } from '@embed/store/tools/tracking';
import { pruneNilValues } from '@embed/store/tools/utils';
import { WidgetTypesEnum, } from '@embed/widgetTypes';
import { PostMessageSentinelEnum, postMessageWrapper, } from '@src/embeds/utils/postMessageWrapper';
const embedFeedRenderPromise = (...args) => import('@embed/embedFeedRender').then((module) => module.default(...args));
const storyblockRenderPromise = (...args) => import('@embed/storyblockRender').then((module) => module.default(...args));
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export default ((fwn) => (w = window, options = {
    modules: [
        'analytics',
        'console',
        'play',
        'players',
        'render',
        'playersStateSync',
        'useGlobalStore',
        'hydrate',
    ],
}) => {
    var _a, _b, _c;
    if (!w._fwn) {
        fwn = w._fwn = function () {
            fwn.callMethod
                ? // eslint-disable-next-line prefer-rest-params, prefer-spread
                    fwn.callMethod.apply(fwn, arguments)
                : // eslint-disable-next-line prefer-rest-params
                    fwn.queue.push(arguments);
        };
        fwn.queue = [];
    }
    if (options.modules.includes('analytics') &&
        !Object.prototype.hasOwnProperty.call(w._fwn, 'analytics')) {
        // Settup analytics collect fn
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { fwnAnalytics } = require('./fwnAnalytics');
        Object.defineProperty(w._fwn, 'analytics', {
            value: fwnAnalytics(),
            writable: false,
        });
        sendPixelTrackingWithSession({
            url: '/trk/system:page_load',
            data: {},
            host: undefined,
        });
    }
    if (options.modules.includes('console') &&
        !Object.prototype.hasOwnProperty.call(w._fwn, 'console')) {
        // Setting up console
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { fwnConsole } = require('@src/embeds/utils/fwnInit/fwnConsole');
        Object.defineProperty(w._fwn, 'console', {
            value: fwnConsole(w),
            writable: false,
        });
    }
    if (options.modules.includes('hydrate') &&
        !Object.prototype.hasOwnProperty.call(w._fwn, 'hydrate')) {
        const video = (callback) => {
            const partialVideo = videoFactory(callback);
            videoHydratedAction(getGlobalStore(), partialVideo);
        };
        Object.defineProperty(w._fwn, 'hydrate', {
            value: {
                video,
            },
            writable: false,
        });
    }
    if (options.modules.includes('play') &&
        !Object.prototype.hasOwnProperty.call(w._fwn, 'play')) {
        // Player trigger
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { fwnPlay } = require('./fwnPlay');
        Object.defineProperty(w._fwn, 'play', {
            value: fwnPlay(w),
            writable: false,
        });
    }
    if (options.modules.includes('players') &&
        !Object.prototype.hasOwnProperty.call(w._fwn, 'players')) {
        // Setting up access to video
        Object.defineProperty(w._fwn, 'players', {
            get: () => w.parent['_fwn_playerVideo'],
        });
    }
    if (options.modules.includes('render') &&
        !Object.prototype.hasOwnProperty.call(w._fwn, 'render')) {
        // Create cache for emotion
        const emotionCache = createEmbedEmotionCache({
            container: w.document.head,
            speedy: true,
        });
        // Setting up render (legacy)
        Object.defineProperty(w._fwn, 'render', {
            value: (params) => {
                if (params['type'] === 'storyblock') {
                    return storyblockRenderPromise(params, emotionCache);
                }
                return embedFeedRenderPromise(params, emotionCache);
            },
            writable: false,
        });
    }
    if (options.modules.includes('playersStateSync') &&
        !Object.prototype.hasOwnProperty.call(w._fwn, 'playersStateSync')) {
        let playersStateSync;
        try {
            playersStateSync = ((_a = window.parent._fwn) === null || _a === void 0 ? void 0 : _a.playersStateSync) || {};
        }
        catch (e) {
            playersStateSync = {};
        }
        const dispatchGlobalPlayersState = (state) => {
            postMessageWrapper({
                action: GLOBAL_PLAYERS_STATE_SYNC,
                sentinel: PostMessageSentinelEnum.INTERNAL,
                state,
            });
        };
        const listener = (event) => {
            var _a;
            if (((_a = event.data) === null || _a === void 0 ? void 0 : _a.sentinel) === PostMessageSentinelEnum.INTERNAL) {
                const { action, playerState, widgetId } = event.data;
                switch (action) {
                    case UPDATE_PLAYER_STATE_SYNC: {
                        if (!playerState) {
                            return;
                        }
                        const newPriority = openReasonPriority[playerState.openReason];
                        for (const id of Object.keys(playersStateSync)) {
                            if (id === playerState.widget_id) {
                                continue;
                            }
                            if (openReasonPriority[playersStateSync[id].openReason] <=
                                newPriority) {
                                playersStateSync[id].openReason =
                                    VideoPlayerOpenReason.NOT_OPENED;
                                playersStateSync[id].layout = PlayerLayout.RELATIVE;
                            }
                        }
                        playersStateSync[playerState.widget_id] = playerState;
                        dispatchGlobalPlayersState(playersStateSync);
                        break;
                    }
                    case REMOVE_PLAYER_STATE_SYNC: {
                        delete playersStateSync[widgetId];
                        dispatchGlobalPlayersState(playersStateSync);
                        break;
                    }
                    default:
                        return;
                }
            }
        };
        try {
            window.parent.addEventListener('message', listener);
        }
        catch (e) {
            window.addEventListener('message', listener);
        }
        Object.defineProperty(w._fwn, 'playersStateSync', {
            value: playersStateSync,
            writable: false,
        });
    }
    try {
        if ((_b = w.parent._fwn) === null || _b === void 0 ? void 0 : _b.initialized) {
            return;
        }
    }
    catch (_d) {
        if ((_c = w._fwn) === null || _c === void 0 ? void 0 : _c.initialized) {
            return;
        }
    }
    w._fwn.initialized = true;
    /**
     * Restore scroll if player was closed in PIP
     * can't use storage hook here, not a functional component.
     */
    const scrollY = window.sessionStorage.getItem(EnumStorageKeys.IFRAME_SCROLL_POSITION);
    if (scrollY) {
        window.scrollTo(0, +scrollY);
        window.sessionStorage.removeItem(EnumStorageKeys.IFRAME_SCROLL_POSITION);
    }
    /**
     *
     *  Resolve initial video cascade
     *    - from url param
     *    - from soft PIP player state fromlocal storage
     *
     */
    try {
        /** Resolve video from url hash or query (shared) */
        const fwPlayerParam = parseFWPlayerParam(w);
        if (fwPlayerParam && !w['_fwn_fetchingInitialVideo']) {
            w['_fwn_fetchingInitialVideo'] = fwPlayerParam;
            const maxVideos = fwPlayerParam.app_id ||
                fwPlayerParam.channel ||
                fwPlayerParam.playlist_id ||
                fwPlayerParam.hashtag
                ? undefined
                : 1;
            return (w._fwn.play &&
                w._fwn.play(Object.assign({ app_context_type: AppContextType.EMBED_CONTEXT_TYPE, type: WidgetTypesEnum.PROGRAMMATIC }, pruneNilValues({
                    app_id: fwPlayerParam.app_id,
                    channel: fwPlayerParam.channel,
                    hashtag: fwPlayerParam.hashtag,
                    max_videos: maxVideos,
                    playlist: fwPlayerParam.playlist_id,
                    video: fwPlayerParam.video_id,
                    widget_id: uuidv4(),
                    start_time: fwPlayerParam.start_time,
                })), {
                    // Lets try to get unmuted/autoplay. If the attempt
                    // fails, globalVideoElement will take care of gracefull
                    // degradation to muted/autoplay
                    // Initial PIP will be disabled by pip=false
                    autoplay: true,
                    muted: false,
                    pip: false,
                }));
        }
    }
    catch (err) {
        logError(err);
    }
    try {
        let lastPlayerState = w._fwn['_fwnLstPlrState'];
        if (!lastPlayerState && w.name !== 'fw-iframe') {
            try {
                lastPlayerState = w._fwn['_fwnLstPlrState'] = JSON.parse(w.sessionStorage.getItem('_fwnplrstate') || '{}');
            }
            catch (e) {
                lastPlayerState = w._fwn['_fwnLstPlrState'] = {};
            }
            const { createdAt } = lastPlayerState, storedState = __rest(lastPlayerState, ["createdAt"]);
            if (createdAt) {
                // Last player state is Valid for 10s
                if (createdAt + 10 * 1000 > new Date().getTime()) {
                    logInfo('Restoring player state', lastPlayerState);
                    return (w._fwn.play &&
                        w._fwn.play(Object.assign(Object.assign({}, storedState), { minimized: true, widget_id: uuidv4(), show_preroll: false }), {
                            // Lets try to get unmuted/autoplay. If the attempt
                            // fails, globalVideoElement will take care of gracefull
                            // degradation to muted/autoplay
                            autoplay: true,
                            muted: false,
                            pip: true,
                        }));
                }
                else {
                    throw new Error('Expired player state (TTL 10s)');
                }
            }
        }
    }
    catch (err) {
        logError(err);
    }
})();
